import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { Tooltip } from '@mui/material';

import { Download, Zap } from 'lucide-react';

import { useViewModeTransition } from '../../../context/ViewModeTransitionContext.tsx';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { downloadBlenderProject } from '../../../store/reducers/workspace.reducer.ts';
import { getUser } from '../../../store/selectors/user.selector';
import { selectActiveVersion } from '../../../store/selectors/workspace.selector.ts';
import { useViewModeAvailability } from '../hooks/useViewModeAvailability.ts';
import LightingCustom from '../icons/LightingCustom';
import LightingRender from '../icons/LightingRender';
import RasterIcon from '../icons/RasterIcon';
import { type UrlViewMode, parseViewParam } from './panelUrl';

type ViewMode = 'object' | 'measurement' | 'render' | 'realTimeRender';

/**
 * Scene / Measurement / Render / Real-time switch, floating at the top right of the 3D view
 * (it controls the view, so it sits on it rather than in the side panel). Same availability rules as the legacy
 * sidebar's row; the active mode also shows its name.
 */
export function ViewModeSwitch() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { transitionToMode } = useViewModeTransition();
  const user = useSelector(getUser);
  const version = useAppSelector(selectActiveVersion);
  const projectId = useAppSelector((s) => s.workspace.project?.id);
  const activeViewModes = useAppSelector((s) => s.workspace.activeViewModes);
  const { webGpuSupported, fullyAssigned, measurementAvailable, renderAvailable, realTimeAvailable } =
    useViewModeAvailability();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentMode: ViewMode =
    (['measurement', 'render', 'realTimeRender'] as const).find((m) => activeViewModes?.[m]) ?? 'object';

  // `view` in the URL: a shared link opens that mode as soon as it is available (measurement data
  // loads after the model), and the URL follows the mode the user picks.
  const viewIntent = useRef<UrlViewMode | null>(parseViewParam(searchParams.get('view')));
  const intentVersion = useRef(version?.id);
  useEffect(() => {
    if (intentVersion.current != null && intentVersion.current !== version?.id) viewIntent.current = null;
    intentVersion.current = version?.id;
  }, [version?.id]);

  useEffect(() => {
    const wanted = viewIntent.current;
    if (!wanted) return;
    const available =
      wanted === 'measurement' ? measurementAvailable : wanted === 'render' ? renderAvailable : realTimeAvailable;
    if (!available) return;
    viewIntent.current = null;
    if (currentMode !== wanted) transitionToMode(wanted);
  }, [measurementAvailable, renderAvailable, realTimeAvailable, currentMode, transitionToMode]);

  useEffect(() => {
    if (viewIntent.current) return;
    const value = currentMode === 'object' ? null : currentMode;
    if (searchParams.get('view') === value) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set('view', value);
        else next.delete('view');
        return next;
      },
      { replace: true },
    );
  }, [currentMode, searchParams, setSearchParams]);

  const pickMode = (mode: ViewMode) => {
    viewIntent.current = null;
    transitionToMode(mode);
  };

  const modes: { id: ViewMode; label: string; icon: JSX.Element; enabled: boolean; tooltip: string }[] = [
    {
      id: 'object',
      label: t('workspace.panel.view.scene'),
      icon: <LightingRender />,
      enabled: true,
      tooltip: t('workspace.viewMode.scene'),
    },
    {
      id: 'measurement',
      label: t('workspace.panel.view.measurement'),
      icon: <RasterIcon size={20} />,
      enabled: measurementAvailable,
      tooltip: measurementAvailable
        ? t('workspace.viewMode.measurement')
        : t('workspace.viewMode.measurementNotAvailable'),
    },
    {
      id: 'render',
      label: t('workspace.panel.view.render'),
      icon: <LightingCustom />,
      enabled: renderAvailable,
      tooltip: renderAvailable
        ? t('workspace.viewMode.render')
        : fullyAssigned
          ? t('workspace.viewMode.renderNotAvailableMarketMatched')
          : t('workspace.viewMode.renderNotAvailable'),
    },
    {
      id: 'realTimeRender',
      label: t('workspace.panel.view.realTime'),
      icon: <Zap size={20} />,
      enabled: realTimeAvailable,
      tooltip:
        webGpuSupported === false
          ? t('workspace.viewMode.realTimeRenderUnsupported')
          : realTimeAvailable
            ? t('workspace.viewMode.realTimeRender')
            : t('workspace.viewMode.realTimeRenderNotAvailable'),
    },
  ];

  return (
    <div className="absolute top-4 right-4 z-40 flex items-center gap-1.5">
      {activeViewModes?.render && user?.roles?.includes('ADMIN') && (
        <Tooltip title={t('workspace.downloadBlenderProject')} placement="bottom" arrow>
          <button
            type="button"
            aria-label={t('workspace.downloadBlenderProject')}
            disabled={projectId == null || !version}
            onClick={() => {
              if (projectId != null && version) dispatch(downloadBlenderProject({ projectId, versionId: version.id }));
            }}
            className="h-11 w-11 flex items-center justify-center rounded-xl bg-[#1E1E1E] border border-white/10 shadow-2xl text-gray-200 hover:text-white disabled:opacity-40"
          >
            <Download size={18} />
          </button>
        </Tooltip>
      )}
      <div
        role="radiogroup"
        aria-label={t('workspace.panel.view.label')}
        className="flex items-center gap-0.5 p-1 rounded-xl bg-[#1E1E1E] border border-white/10 shadow-2xl"
      >
        {modes.map((mode) => {
          const active = !!activeViewModes?.[mode.id];
          return (
            <Tooltip key={mode.id} title={mode.tooltip} placement="bottom" arrow>
              <span>
                <button
                  type="button"
                  role="radio"
                  aria-checked={active}
                  aria-label={mode.label}
                  disabled={!mode.enabled}
                  onClick={() => pickMode(mode.id)}
                  className={`h-9 flex items-center gap-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                    active
                      ? 'pl-2 pr-3 bg-white/10 text-lime-400'
                      : 'px-2 text-gray-200 hover:text-white hover:bg-white/5 disabled:hover:bg-transparent'
                  }`}
                >
                  <span className="flex h-5 w-5 items-center justify-center">{mode.icon}</span>
                  {active && <span className="text-[13px] font-medium whitespace-nowrap">{mode.label}</span>}
                </button>
              </span>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}
